/* Module does one thing connect to websocket server emits actions to be excuted when recieved  */
const initialState = {
  is_connected: false,
  is_reconnecting: false,
  queue: [],
  joined_streams: [],
  heart_beat_interval: 120000, //2 min
  reconnect_check_interval: 10000, // 10 seconds
  retry_limit: 30, // Equals 5 minutes when using the reconnect_check_interval
  retry_count: 0,
  socket: null,
  pulse: null,
  check: null,
  intentionalClose: false // To distinguish between intentional and unintentional disconnects
}

export const realtime = {
  namespaced: true,
  state: initialState,
  actions: {
    connect({ dispatch, state, commit, rootState }) {
      if (state?.socket?.readyState != 1 && rootState.auth.status.isLoggedIn && rootState.auth?.token) {
        state.socket = new WebSocket(
          `${import.meta.env.VITE_BASE_API_URL.replace(
            /^https?:\/\//,
            window.location.protocol === "https:" ? "wss://" : "ws://"
          )}/stream/?q=${rootState.auth?.token}`
        )
        state.socket.onopen = () => {
          state.is_connected = true
          state.is_reconnecting = false
          state.retry_count = 0 // Reset retry count on successful connection
          console.debug("\u2665")
          clearInterval(state.check)
          dispatch("updateLiveStatus", { status: "on" }, { root: true })
          state.pulse = setInterval(() => {
            if (state.socket.readyState == 1) {
              state.socket.send(JSON.stringify({ event: "PING" }))
            } else {
              clearInterval(state.pulse)
              dispatch("connect")
            }
          }, state.heart_beat_interval)

          // Only consume the queue if the socket is connected
          if (state.socket.readyState == 1) {
            while (state.queue.length > 0) {
              const item = state.queue.shift()
              state.socket.send(JSON.stringify(item))
            }
          }
        }
        state.socket.onerror = () => {
          state.is_connected = false
          state.retry_count += 1 // Increase the retry count
          if (state.retry_count <= state.retry_limit) {
            // Check if we've reached the retry limit
            if (!state.is_reconnecting) {
              state.check = setInterval(() => {
                console.debug("\u2661")
                dispatch("connect")
              }, state.reconnect_check_interval)
              state.is_reconnecting = true
            }
          } else {
            clearInterval(state.check) // Stop retrying after reaching the retry limit
          }
          dispatch("updateLiveStatus", { status: "off" }, { root: true })
        }

        state.socket.onclose = () => {
          state.is_connected = false
          state.retry_count += 1 // Increase the retry count
          state.socket.close()
          clearInterval(state.pulse)
          if (state.retry_count <= state.retry_limit) {
            // Check if we've reached the retry limit
            if (!state.is_reconnecting) {
              state.check = setInterval(() => {
                console.debug("\u2661")
                dispatch("connect")
              }, state.reconnect_check_interval)
              state.is_reconnecting = true
            }
          } else {
            clearInterval(state.check) // Stop retrying after reaching the retry limit
          }
          dispatch("updateLiveStatus", { status: "off" }, { root: true })
        }

        state.socket.onmessage = ({ data }) => {
          let p_data = JSON.parse(data)
          // show incoming data only in development
          if (import.meta.env.MODE === "development" && p_data.event != "PONG") {
            console.debug("ws", p_data)
          }
          if (p_data.event == "PONG") {
            console.debug("\u2665")
            dispatch("updateLiveStatus", { status: "on" }, { root: true })
          } else if (p_data.event == "VOICE_TRANSCRIPTION") {
            // Handle voice-to-text transcription results
            dispatch("handleVoiceTranscription", p_data?.content, { root: true })
          } else if (p_data.event == "PREDEFINED_ACTION") {
            dispatch(p_data.name, p_data?.content, { root: true })
          } else if (p_data.event == "CREATE") {
            dispatch(p_data.name, p_data?.content, { root: true })
          } else if (p_data.event == "UPDATE") {
            dispatch(`${p_data.name}.${p_data.event}`, p_data?.content, { root: true })
          } else {
            commit(p_data.event, p_data)
          }
        }
      }
    },
    disconnect({ state }) {
      if (state.is_connected) {
        try {
          state.socket.close()
        } catch (error) {
          // console.error(error)
        }
      }
    },
    send({ state }, data) {
      try {
        state.socket.send(JSON.stringify(data))
      } catch (error) {
        state.queue.push(data)
      }
    },
    joinLayoutStreams({ dispatch }, portfolio) {
      // join portfolio layouts streams
      // shared by other users
      let streams_ids = portfolio.shared_layouts.map((l) => l.id)
      // add the organization_id to the streams_ids
      streams_ids = streams_ids.concat(portfolio.organization)
      // shared by the user himself
      Object.entries(portfolio.pages_layout).forEach(([, layouts]) => {
        if (layouts?.length) {
          streams_ids = streams_ids.concat(layouts?.filter((l) => l.is_shared)?.map((l) => l.id))
        }
      })
      streams_ids.forEach((stream_id) => {
        dispatch("send", { event: "JOIN_STREAM", stream_id: stream_id })
        console.debug(`Joined stream ${stream_id}`)
      })
    },
    joinStreams({ dispatch }, streams_ids) {
      // join portfolio layouts streams
      streams_ids.forEach((stream_id) => {
        dispatch("send", { event: "JOIN_STREAM", stream_id: stream_id })
      })
    },
    leaveStreams({ dispatch }, streams_ids) {
      // join portfolio layouts streams
      streams_ids.forEach((stream_id) => {
        dispatch("send", { event: "LEAVE_STREAM", stream_id: stream_id })
      })
    },
    joinStream({ dispatch }, stream_id) {
      dispatch("send", { event: "JOIN_STREAM", stream_id: stream_id })
    },
    leaveStream({ dispatch }, stream_id) {
      dispatch("send", { event: "LEAVE_STREAM", stream_id: stream_id })
    },
    messageStream({ rootState, dispatch }, { event = "MESSAGE_STREAM", stream_id, data }) {
      dispatch("send", { event: event, stream_id: stream_id, data: data, user_id: rootState.auth.user.id })
    }
  },

  mutations: {
    stateReset(state) {
      Object.assign(state, initialState)
    },
    // Voice-related mutations
    VOICE_START_RECORDING(state, data) {
      // Handle voice recording start
      console.log("Voice recording started:", data)
    },
    VOICE_STOP_RECORDING(state, data) {
      // Handle voice recording stop
      console.log("Voice recording stopped:", data)
    },
    VOICE_TRANSCRIPTION(state, data) {
      // Handle voice transcription result
      console.log("Voice transcription received:", data)
    },
    VOICE_ERROR(state, data) {
      // Handle voice error
      console.error("Voice error:", data)
    }
  }
}
